import { defineStore } from 'pinia';
import { isRef, readonly } from 'vue';

import { CallLog } from '@bv/api';
import type { SipOptions } from '@bv/plugins';
import { useUserStore } from '@bv/stores/user';
import { ConnectionStatus, type Normalize, RegistrationStatus, sip } from '@bv/utils';

import type { Invitation, Inviter, SessionState } from 'sip.js';

type CallStatus =
  | SessionState
  | 'calling'
  | 'ringing'
  | 'busy'
  | 'unavailable'
  | 'no_answer'
  | 'not_found'
  | 'answered'
  | 'answered'
  | 'canceled'
  | 'missed';
type CallDirection = 'inbound' | 'outbound';

export interface Call {
  id: string;
  caller: {
    name?: string;
    number?: string;
  };
  direction: CallDirection;
  session: Invitation | Inviter;
  ringAudioElement?: HTMLAudioElement;
  remoteAudioElement?: HTMLAudioElement;
  status?: CallStatus;
  isEstablished?: boolean;
  isMute?: boolean;
  isHold?: boolean;
  isPrevious?: boolean;
  isListen?: boolean;
  isListenAndWhisper?: boolean;
  isActive?: boolean;
  isCanceled?: boolean;
  isAnswered?: boolean;
  audioStream?: MediaStream;
  endCallReason?: string; // TODO
  rejectReasonText?: string; // TODO
  cdr: Cdr;
}

interface NormalizerWithOriginal extends Normalize {
  number: string | number;
}

export interface Cdr {
  startTime: string;
  endCallTime: string;
  startTimeAnswer: string;
  direction: CallDirection;
  fromNumber: string | number;
  toNumber: string | number;
  status: string;
  normalization?: NormalizerWithOriginal;
}

/**
 * Manages the entire SIP flow.
 */
export const useSipStore = defineStore('bvSip', {
  state: () => ({
    _currentCalls: {} as Record<string, Call>,
    _recentlyAddedLog: undefined as CallLog | undefined,
    _options: undefined as SipOptions | undefined,
  }),
  getters: {
    options(): SipOptions | undefined {
      return this._options ? readonly(this._options) : undefined;
    },
    isConnected(): boolean {
      return this.connectionStatus === 'Connected';
    },
    isRegistered(): boolean {
      return this.connectionStatus === 'Connected' && this.registrationStatus === 'Registered';
    },
    connectionStatus(): ConnectionStatus {
      return sip.connectionStatus.value;
    },
    registrationStatus(): RegistrationStatus {
      return sip.registrationStatus.value;
    },
    currentCalls(state): Record<string, Call> {
      return state._currentCalls;
    },
    activeCall(state): Call | undefined {
      return Object.values(state._currentCalls).find((call) => call.isActive);
    },
    previousCall(state): Call | undefined {
      return Object.values(state._currentCalls).find((call) => call.isPrevious);
    },
    lastCall(state): Call | undefined {
      return Object.values(state._currentCalls).at(-1);
    },
    recentlyAddedLog(state): CallLog | undefined {
      return state._recentlyAddedLog;
    },
  },
  actions: {
    /**
     * Set the SIP options.
     *
     * @param {SipOptions | undefined} options - The options to set.
     * @returns {void}
     */
    setOptions(options?: SipOptions): void {
      const hide = options?.widget?.props?.hide;
      const normalizedHide = isRef(hide) ? hide.value : hide;

      this._options = options
        ? {
            ...options,
            port: options.port || 8089,
            connection: options.connection ?? 'connect',
            widget: options.widget
              ? {
                  ...options.widget,
                  props: options.widget.props
                    ? {
                        ...options.widget.props,
                        hide: normalizedHide,
                      }
                    : options.widget.props,
                }
              : options.widget,
          }
        : undefined;
    },
    /**
     * Adding a call to current calls object.
     *
     * @param {string} id - ID of call.
     * @param {Inviter | Invitation} session - (invitation) in case incoming call, or (inviter) in case outbound call.
     * @param {CallDirection} direction - (in) for incoming call, and (out) for outbound call.
     * @returns {Call} - Return created call
     */
    addCall(id: string, session: Inviter | Invitation, direction: CallDirection = 'inbound'): Call {
      Object.values(this._currentCalls).forEach((call) => {
        call.isPrevious = true;
        call.isActive = false;
      });

      const extension: string = useUserStore().extensionDetails?.number || '';
      const name: string = session.assertedIdentity?.displayName || '';
      const number: string = session.remoteIdentity.uri.user || '';

      this._currentCalls[id] = {
        id,
        caller: {
          name,
          number,
        },
        direction,
        session,
        isActive: true,
        isListen: number?.toLowerCase().includes('listen|'),
        isListenAndWhisper: number?.toLowerCase().includes('listen&whisper|'),
        cdr: {
          startTime: '',
          endCallTime: '',
          startTimeAnswer: '',
          direction,
          fromNumber: direction === 'outbound' ? extension : number,
          toNumber: direction === 'outbound' ? number : extension,
          status: '',
        },
      };
      return this._currentCalls[id];
    },
    /**
     * Get a call details as object.
     *
     * @param {string} id - ID of call.
     * @returns {Call} - Return call
     */
    getCall(id: string): Call | undefined {
      return this._currentCalls[id];
    },
    /**
     * Remove a call from current calls and set previous call as active.
     *
     * @param {Call} call - Call.
     */
    removeCall(call: Call): void {
      setTimeout(() => {
        delete this._currentCalls[call.id];

        if (this.previousCall) {
          this.previousCall.isActive = true;
          this.previousCall.isPrevious = false;
        }
      }, 2000);
    },
    /**
     * Store recently added.
     *
     * @param {CallLog} log - Recent ended log.
     */
    setRecentlyAddedLog(log: CallLog): void {
      this._recentlyAddedLog = log;
    },
    /**
     * Set a call is active call and change other current calls to inactive.
     *
     * @param {Call} toBeActiveCall - Call.
     */
    activateCall(toBeActiveCall: Call): void {
      Object.values(this.currentCalls).forEach((call) => {
        if (call.id !== toBeActiveCall.id) {
          call.isActive = false;
          call.isPrevious = true;
        }
      });

      if (toBeActiveCall) {
        toBeActiveCall.isActive = true;
        toBeActiveCall.isPrevious = false;
      }
    },
  },
});
